/**
 * Validates phone numbers (Italian and international)
 * Accepts formats like:
 * - 3331234567 (Italian mobile)
 * - +39 333 123 4567 (Italian with country code)
 * - +1 555 123 4567 (US)
 * - +44 7911 123456 (UK)
 * - +33 6 12 34 56 78 (France)
 */

// Clean phone number from all non-digit characters except leading +
export function cleanPhoneNumber(phone: string): string {
  // Keep only digits and leading +
  const cleaned = phone.replace(/[^\d+]/g, '');
  return cleaned;
}

// Validate phone number format (Italian or international)
export function validateItalianPhone(phone: string): { isValid: boolean; error?: string } {
  if (!phone || !phone.trim()) {
    return { isValid: false, error: 'Il numero di telefono è richiesto' };
  }

  const cleaned = cleanPhoneNumber(phone);
  
  // Check if it has international prefix (starts with +)
  if (cleaned.startsWith('+')) {
    // International number: should have at least 8 digits after the +
    const digitsOnly = cleaned.slice(1);
    if (digitsOnly.length < 8) {
      return { 
        isValid: false, 
        error: 'Il numero internazionale deve avere almeno 8 cifre' 
      };
    }
    if (digitsOnly.length > 15) {
      return { 
        isValid: false, 
        error: 'Il numero di telefono è troppo lungo' 
      };
    }
    return { isValid: true };
  }
  
  // Italian mobile (no prefix): starts with 3, 10 digits
  if (cleaned.startsWith('3')) {
    if (cleaned.length !== 10) {
      return { 
        isValid: false, 
        error: 'Il numero di cellulare italiano deve avere 10 cifre (es. 333 123 4567)' 
      };
    }
    return { isValid: true };
  }
  
  // Italian landline (no prefix): starts with 0, 9-11 digits
  if (cleaned.startsWith('0')) {
    if (cleaned.length < 9 || cleaned.length > 11) {
      return { 
        isValid: false, 
        error: 'Il numero fisso italiano deve avere tra 9 e 11 cifre' 
      };
    }
    return { isValid: true };
  }
  
  // Generic number without prefix: must have at least 7 digits
  if (cleaned.length >= 7 && cleaned.length <= 15) {
    return { isValid: true };
  }
  
  return { 
    isValid: false, 
    error: 'Inserisci un numero valido. Per numeri esteri usa il prefisso (es. +44, +33, +1)' 
  };
}

// Format phone number for display
export function formatPhoneNumber(phone: string): string {
  const cleaned = cleanPhoneNumber(phone);
  
  // If it has country code
  if (cleaned.startsWith('+39')) {
    const number = cleaned.slice(3);
    if (number.length === 10) {
      return `+39 ${number.slice(0, 3)} ${number.slice(3, 6)} ${number.slice(6)}`;
    }
    return `+39 ${number}`;
  }
  
  // Format 10-digit Italian mobile number
  if (cleaned.length === 10 && cleaned.startsWith('3')) {
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
  }
  
  // For international numbers, just return with spaces
  if (cleaned.startsWith('+')) {
    return phone; // Keep original formatting
  }
  
  return phone;
}
